import { useState } from "react";
import { Camera, X } from "lucide-react";

/**
 * Sprint 73-fix — reusable proof photo gallery.
 *
 * Renders 0–6 proof photos as thumbnails. Clicking any opens a lightbox.
 * Shows graceful empty state if no photos yet.
 *
 * Props:
 *   order      — full order doc (must have *_photo fields and/or proof_events)
 *   size       — "sm" | "md" (default md)
 *   showLabel  — bool, prepend "Proofs:" label
 */
export default function ProofGallery({ order, size = "md", showLabel = false }) {
  const [open, setOpen] = useState(null);
  const photos = collectPhotos(order);

  if (photos.length === 0) return null;

  const dim = size === "sm" ? "w-12 h-12" : "w-16 h-16";

  return (
    <div className="inline-flex items-center gap-1.5 flex-wrap" data-testid="proof-gallery">
      {showLabel && (
        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mr-1">
          <Camera className="h-3 w-3 inline -mt-0.5" /> Proofs
        </span>
      )}
      {photos.map((p, i) => (
        <button
          key={`${p.kind}-${i}`}
          type="button"
          onClick={() => setOpen(p)}
          className={`${dim} rounded-lg border border-slate-200 overflow-hidden hover:border-orange-300 transition relative group`}
          title={p.label}
          data-testid={`proof-thumb-${p.kind}`}
        >
          <img src={p.src} alt={p.label} className="w-full h-full object-cover" />
          <span className="absolute inset-x-0 bottom-0 bg-black/55 text-white text-[8px] py-0.5 px-1 text-center truncate group-hover:bg-black/75">
            {p.short}
          </span>
        </button>
      ))}

      {open && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setOpen(null)}
          data-testid="proof-lightbox"
        >
          <div className="relative max-w-4xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setOpen(null)}
              className="absolute -top-3 -right-3 bg-white rounded-full p-1.5 shadow-lg"
              data-testid="proof-lightbox-close"
            >
              <X className="h-4 w-4" />
            </button>
            <img src={open.src} alt={open.label} className="max-w-full max-h-[80vh] rounded-xl shadow-2xl" />
            <div className="text-white text-center mt-3">
              <div className="font-semibold">{open.label}</div>
              {open.ts && <div className="text-xs text-white/70">{new Date(open.ts).toLocaleString("en-IN")}</div>}
              {open.by && <div className="text-xs text-white/70">by {open.by}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Sprint 73-fix: only render images that are real, embeddable sources.
// Older orders persisted `blob:` URLs (transient browser handles) which
// are unreachable outside the original tab and would show as broken.
function isRenderableImageSrc(s) {
  if (!s || typeof s !== "string") return false;
  return s.startsWith("data:image/") || s.startsWith("http://") || s.startsWith("https://");
}

function collectPhotos(o) {
  if (!o) return [];
  const out = [];
  if (isRenderableImageSrc(o.customer_package_photo)) {
    out.push({
      kind: "customer", short: "Customer", label: "Customer's package photo at booking",
      src: o.customer_package_photo, ts: o.customer_package_photo_at, by: "Customer",
    });
  }
  if (isRenderableImageSrc(o.origin_hub_handover_photo)) {
    out.push({
      kind: "handover", short: "Pickup", label: "Pickup handover at origin hub",
      src: o.origin_hub_handover_photo, ts: o.origin_hub_handover_at, by: "Pickup agent",
    });
  }
  if (isRenderableImageSrc(o.packing_photo)) {
    out.push({
      kind: "packing", short: "Packed", label: "Packed at hub",
      src: o.packing_photo, ts: o.packed_at, by: "Hub staff",
    });
  }
  if (isRenderableImageSrc(o.destination_arrival_photo)) {
    out.push({
      kind: "arrival", short: "Arrived", label: "Arrived at destination hub",
      src: o.destination_arrival_photo, ts: o.destination_arrival_at, by: "Destination staff",
    });
  }
  // proof_events may carry extras
  for (const ev of o.proof_events || []) {
    if (!isRenderableImageSrc(ev.image_base64)) continue;
    const key = ev.type;
    // Sprint-93 — dedupe by photo source AND by kind. The customer photo
    // is stored both top-level (`customer_package_photo`) and again in
    // `proof_events` with type=customer_package_photo, which was causing
    // the same image to render twice in OrderDetails.
    if (out.find((p) => p.kind === key || p.src === ev.image_base64)) continue;
    out.push({
      kind: key, short: (ev.label || "").split(" ")[0], label: ev.label,
      src: ev.image_base64, ts: ev.ts, by: ev.by_name || ev.by_role,
    });
  }
  return out;
}
